import { RequiredKeys } from '@main/shared/types';

import { AsyncIteratorCtor } from './async-iterable';
import { signalToPromise } from './promise-utils';

export interface AbortableOptions {
  signal?: AbortSignal;
  isReused?: boolean;
}

/**
 * Wraps an {@link AsyncIterable} to make it abortable immediately.
 */
export function abortable<T extends AsyncIterable<never>>(iterable: T): T;
export function abortable<T, TReturn = never, TNext = never>(
  iterable: AsyncIterable<T, TReturn, TNext | AbortSignal | undefined>,
  options?: AbortableOptions,
): AbortableAsyncIterable<T, TReturn, TNext>;
export function abortable<T, TReturn = never, TNext = never>(
  iterable: AsyncIterable<T, TReturn, TNext | AbortSignal | undefined>,
  options?: AbortableOptions,
) {
  if (!options?.signal) {
    return iterable;
  }

  return new AbortableAsyncIterable(iterable, options as AbortableAsyncOptions);
}

export type AbortableAsyncOptions = RequiredKeys<AbortableOptions, 'signal'>;

export class AbortableAsyncIterable<T, TReturn = never, TNext = never>
  implements AsyncIterable<T, TReturn | undefined, TNext | undefined>
{
  constructor(
    protected iterable: AsyncIterable<T, TReturn, TNext | AbortSignal | undefined>,
    protected options: AbortableAsyncOptions,
  ) {}

  [Symbol.asyncIterator]() {
    return new AbortableAsyncIterator(this.iterable, this.options);
  }
}

export class AbortableAsyncIterator<T, TReturn = never, TNext = never>
  extends AsyncIteratorCtor
  implements AsyncIterableIterator<T, TReturn | undefined, TNext | undefined>
{
  protected signalPromise;
  protected iterator;

  constructor(
    protected iterable: AsyncIterable<T, TReturn, TNext | AbortSignal | undefined>,
    protected options: AbortableAsyncOptions,
  ) {
    super();

    this.options.signal.throwIfAborted();
    this.signalPromise = signalToPromise(this.options.signal);
    this.iterator = iterable[Symbol.asyncIterator]();
  }

  override [Symbol.asyncIterator]() {
    if (this.options.isReused) {
      return this;
    }

    return new AbortableAsyncIterator(this.iterable, this.options) as this;
  }

  next(value?: TNext): Promise<IteratorResult<T, TReturn>> {
    return Promise.race([this.iterator.next(value ?? this.options.signal), this.signalPromise]);
  }

  async return(
    value?: TReturn | PromiseLike<TReturn>,
  ): Promise<IteratorResult<T, TReturn | undefined>> {
    this.dispose();
    return this.iterator.return?.(value) ?? { done: true, value: await value };
  }

  async throw(error?: unknown): Promise<IteratorResult<T, TReturn | undefined>> {
    this.dispose();
    return this.iterator.throw?.(error) ?? { done: true, value: undefined };
  }

  protected dispose() {
    this.signalPromise.dispose();
  }
}
